import type { SdmsImportWrite } from './qcBatch.api';

/** Combine several files' import writes, OG-style: OG stamps each file's parsed columns onto the
 *  matched dtsample row (drSelected[field] = value, :22093-22695), so a later file overwrites only
 *  the FIELDS it supplies and leaves the earlier file's other fields on that row. The sheet
 *  write-back keeps one write per (SysSampleCode|Parameter) row, so rows hit by more than one file
 *  are merged here field-by-field (later file wins). Within ONE file the writes pass through
 *  untouched, so a single-file import behaves exactly as before. */
export function mergeImportWritesAcrossFiles(perFile: SdmsImportWrite[][]): SdmsImportWrite[] {
  if (perFile.length === 1) return perFile[0]!;
  const key = (w: SdmsImportWrite) => `${w.sysSampleCode.trim().toUpperCase()}|${w.parameter.trim().toUpperCase()}`;
  const out: SdmsImportWrite[] = [];
  const byKey = new Map<string, SdmsImportWrite>();
  for (const writes of perFile) {
    // Collapse this file's own writes first (last in the file wins, as the write-back does today).
    const fileRows = new Map<string, SdmsImportWrite>();
    for (const w of writes) fileRows.set(key(w), w);
    for (const [k, w] of fileRows) {
      const prev = byKey.get(k);
      if (!prev) {
        const copy = { ...w, fields: [...w.fields] };
        byKey.set(k, copy); out.push(copy);
        continue;
      }
      for (const f of w.fields) {
        const i = prev.fields.findIndex(p => p.field.toUpperCase() === f.field.toUpperCase());
        if (i >= 0) prev.fields[i] = f; else prev.fields.push(f);
      }
      if (w.dilution != null) prev.dilution = w.dilution;
    }
  }
  return out;
}

/** Stamp the imported file's name as the SHEETNAME value of every row the file matched — OG's import sets
 *  `drSelected["SheetName"] = import.FileName` (SpreadsheetEntryController :22565 / :22695) and the re-bind shows
 *  it in the run sheet's Filename column (VOC AC: `IF(D8="THMs",AC9,"CCV1.txt")`). Without it that column saved
 *  blank (OG vs AI comparison 2026-09-28, point 12). A value the file itself supplies is kept. */
export function withSheetName(writes: readonly SdmsImportWrite[], fileName: string): SdmsImportWrite[] {
  if (!fileName) return [...writes];
  return writes.map(w => w.fields.some(f => f.field.toUpperCase() === 'SHEETNAME')
    ? w
    : { ...w, fields: [...w.fields, { field: 'SHEETNAME', value: fileName }] });
}
